import type { Dispatch } from "react";
import React, { useCallback, useMemo, useRef, useState } from "react";
import {
   Box,
   Flex,
   FlexList,
   Title,
   H2,
   useI18nContext,
   EmptyState,
   Card,
} from "@procore/core-react";
import type { ColumnDefinition, ServerSideGetRowsParams, TableApi } from "@procore/data-table";
import { ServerSideDataTable, DateCellRenderer, DateCellEditor } from "@procore/data-table";
import {
   getAssignmentTableHeight,
   PERSON_ASSIGNMENTS_PAGE_SIZE,
} from "./person-assignments-table-utils";
import { DateFilter } from "../../../data-table/custom-filters/date-filter";
import { ColorSelectRenderer } from "@/react/components/data-table/ColorSelectComponent/ColorSelectColumn";
import { CustomLinkCellRenderer } from "../../../data-table/custom-link-column/custom-link-cell";
import { getCurrentNextAssignmentsFormatted } from "../../assignment/helpers";
import { fetchPastAssignments } from "../../assignment/queries";
import { PageTitle } from "@/react/prop-types";
import {
   getSelectedDay,
   updateAssignment as updateAssignmentHelper,
} from "../../../assignment-list/assignment-list-helpers";
import { formatDateTime } from "@/react/shared/helper";
import { AuthAction, usePermissionContext } from "@/react/providers/permission-context-provider";
import { useToastAlertContext } from "@procore/toast-alert";
import type { ProjectTearsheetActions } from "../../project/project-tearsheet";

interface PastAssignmentsTableProps {
   personId: string;
   projectTearsheetDispatch?: Dispatch<ProjectTearsheetActions>;
   refetchAssignments?: () => void;
}

/** idle | empty | loaded; parent uses `key={personId}` to reset (no effect). */
type PastAssignmentsDataState =
   | { status: "idle" }
   | { status: "empty" }
   | { status: "loaded"; totalCount: number };

const PastAssignmentsTableComponent: React.FC<PastAssignmentsTableProps> = ({
   personId,
   projectTearsheetDispatch,
   refetchAssignments,
}) => {
   const I18n = useI18nContext();
   const { checkAuthAction } = usePermissionContext();
   const canManageAssignments = checkAuthAction(AuthAction.MANAGE_ASSIGNMENTS);
   const { showToast } = useToastAlertContext();

   const nextStartAfter = useRef<string | undefined>(undefined);
   const tableApiRef = useRef<TableApi | null>(null);
   const [dataState, setDataState] = useState<PastAssignmentsDataState>({ status: "idle" });

   const dispatchRef = useRef(projectTearsheetDispatch);
   dispatchRef.current = projectTearsheetDispatch;

   const handleDateCellValueChanged = useCallback(
      async (params: any, fieldType: "start_date" | "end_date") => {
         if (!params.newValue) return;

         const { newValue, rowData } = params;
         const startDate = fieldType === "start_date" ? newValue : rowData.start_date;
         const endDate = fieldType === "end_date" ? newValue : rowData.end_date;

         if (endDate?.getTime() < startDate?.getTime()) return;

         const dateToValidate = fieldType === "start_date" ? startDate : endDate;
         const selectedDay = getSelectedDay(dateToValidate, rowData?.work_days_object);
         if (!selectedDay) return;

         const formattedDate =
            newValue instanceof Date
               ? new Date(
                    formatDateTime(newValue, {
                       locale: "en",
                       isRequiredTimezone: true,
                       timeZone: "UTC",
                    }),
                 )
               : undefined;

         params.newValue = formattedDate;

         try {
            await updateAssignmentHelper(params, null);
            tableApiRef.current?.refreshServerSide({});
            refetchAssignments?.();
         } catch (error: any) {
            showToast.error(
               I18n.t("views.company.workforce_planning.assignment.inline_edit.error"),
            );
            tableApiRef.current?.refreshServerSide({});
         }
      },
      [refetchAssignments],
   );

   const columnDefinitions = useMemo<ColumnDefinition[]>(() => {
      const projectNameColumn: ColumnDefinition = {
         field: "project_name",
         headerName: I18n.t("views.company.workforce_planning.project"),
         cellRenderer: CustomLinkCellRenderer,
         cellRendererParams: {
            pageTitle: PageTitle.PEOPLE_LIST,
            get getProjectDetailDispatch() {
               return dispatchRef.current;
            },
         },
         getStringFormattedValue: (value: any) => value,
         editable: false,
         flex: 15,
         minWidth: 350,
      };

      const jobTitleColumn: ColumnDefinition = {
         field: "job_title",
         cellRenderer: ColorSelectRenderer,
         cellRendererParams: {
            getColor: (item: { color: string }) => item.color,
            getShape: (item: { shape: string }) => item.shape,
         },
         getStringFormattedValue: (item: any) => item.label,
         headerName: I18n.t("views.company.workforce_planning.people.job_title"),
         editable: false,
         minWidth: 150,
      };

      const startDateColumn: ColumnDefinition = {
         field: "start_date",
         headerName: I18n.t("views.company.workforce_planning.start_date"),
         cellRenderer: DateCellRenderer,
         cellRendererParams: {
            timeZone: "UTC",
         },
         cellEditor: DateCellEditor,
         cellEditorParams: {
            timeZone: "UTC",
         },
         filterRenderer: DateFilter as any,
         editable: canManageAssignments,
         flex: 8,
         minWidth: 150,
         valueValidator: (value) => {
            const { value: startDate, data } = value;

            if (!startDate) {
               return {
                  isValid: false,
                  errorMessage: I18n.t(
                     "views.company.workforce_planning.validations.required_field",
                  ),
                  isRequired: true,
               };
            }

            if (data?.end_date && data.end_date < data.start_date) {
               return {
                  isValid: false,
                  errorMessage: I18n.t(
                     "views.company.workforce_planning.assignment.bulk.edit.validation.start_date",
                     {
                        date: formatDateTime(data.end_date as Date, {
                           locale: I18n.locale,
                           isRequiredTimezone: false,
                        }),
                     },
                  ),
                  isRequired: true,
               };
            }

            const selectedDay =
               startDate instanceof Date && !isNaN(startDate.getTime())
                  ? getSelectedDay(startDate, data?.work_days_object)
                  : null;
            if (!selectedDay) {
               return {
                  isValid: false,
                  errorMessage: I18n.t(
                     "views.company.workforce_planning.assignment.bulk.edit.validation.start_date_active_day",
                  ),
                  isRequired: true,
               };
            }

            return { isValid: true, isRequired: true };
         },
         onCellValueChanged: async (params) => {
            await handleDateCellValueChanged(params, "start_date");
         },
      };

      const endDateColumn: ColumnDefinition = {
         field: "end_date",
         headerName: I18n.t("views.company.workforce_planning.end_date"),
         cellRenderer: DateCellRenderer,
         cellRendererParams: {
            timeZone: "UTC",
         },
         cellEditor: DateCellEditor,
         cellEditorParams: {
            timeZone: "UTC",
         },
         filterRenderer: DateFilter as any,
         editable: canManageAssignments,
         flex: 8,
         minWidth: 150,
         valueValidator: (value) => {
            const { value: endDate, data } = value;
            if (!endDate) {
               return {
                  isValid: false,
                  errorMessage: I18n.t(
                     "views.company.workforce_planning.validations.required_field",
                  ),
                  isRequired: true,
               };
            }
            if (data?.end_date != null && data?.end_date < data?.start_date) {
               return {
                  isValid: false,
                  errorMessage: I18n.t(
                     "views.company.workforce_planning.validations.end_date_before_start_date",
                  ),
                  isRequired: true,
               };
            }

            const selectedDay =
               endDate instanceof Date && !isNaN(endDate.getTime())
                  ? getSelectedDay(endDate, data?.work_days_object)
                  : null;
            if (!selectedDay) {
               return {
                  isValid: false,
                  errorMessage: I18n.t(
                     "views.company.workforce_planning.assignment.bulk.edit.validation.end_date_active_day",
                  ),
                  isRequired: true,
               };
            }

            return { isValid: true };
         },
         onCellValueChanged: async (params) => {
            await handleDateCellValueChanged(params, "end_date");
         },
      };

      return [projectNameColumn, jobTitleColumn, startDateColumn, endDateColumn];
   }, [I18n]);

   const handleServerSideDataRequest = useCallback(
      async ({ request, onSuccess, onError }: ServerSideGetRowsParams) => {
         try {
            let startingAfter = undefined;
            if (request && request.startRow && request.startRow > 0) {
               startingAfter = nextStartAfter.current;
            }

            const sortModel = request.sortModel;
            const data = await fetchPastAssignments(personId, sortModel, startingAfter);

            if (!data) {
               setDataState({ status: "empty" });
               onSuccess({ rowData: [], rowCount: 0 });
               return;
            }

            const formattedData = getCurrentNextAssignmentsFormatted(data.data);
            const count = data?.pagination?.total_possible || 0;

            setDataState(count > 0 ? { status: "loaded", totalCount: count } : { status: "empty" });

            onSuccess({
               rowData: formattedData || [],
               rowCount: count,
            });

            nextStartAfter.current = data?.pagination?.next_starting_after || undefined;
         } catch (err) {
            onError(); // grid handles error UX; same as other server-side tables
         }
      },
      [personId],
   );

   const totalCount = dataState.status === "loaded" ? dataState.totalCount : 0;
   const { tableHeight, showPagination } = getAssignmentTableHeight(
      totalCount,
      PERSON_ASSIGNMENTS_PAGE_SIZE,
   );
   const showEmptyState = dataState.status === "empty";

   const pastAssignmentsSectionTitle = (
      <FlexList size="xs" alignItems="center" justifyContent="space-between" paddingBottom="md">
         <Box>
            <Title>
               <Title.Text>
                  <H2>
                     {I18n.t(
                        "views.company.workforce_planning.people.assignments_tab.past_assignments",
                     )}
                  </H2>
               </Title.Text>
            </Title>
         </Box>
      </FlexList>
   );

   return (
      <Card style={{ marginBottom: "16px", overflow: "hidden" }}>
         <Box padding="sm">
            {pastAssignmentsSectionTitle}
            {showEmptyState ? (
               <EmptyState compact>
                  <EmptyState.NoItems />
                  <EmptyState.Title>
                     {I18n.t(
                        "views.company.workforce_planning.people.assignments_tab.past_assignments_title",
                     )}
                  </EmptyState.Title>
                  <EmptyState.Description>
                     {I18n.t(
                        "views.company.workforce_planning.people.assignments_tab.past_assignments_description",
                     )}
                  </EmptyState.Description>
               </EmptyState>
            ) : (
               <ServerSideDataTable
                  columnDefinitions={columnDefinitions}
                  onServerSideDataRequest={handleServerSideDataRequest}
                  getRowId={(params) => params.data.id}
               >
                  <Flex style={{ width: "100%" }} alignItems="stretch">
                     <Box
                        flex="1"
                        display="flex-column"
                        alignItems="stretch"
                        style={{
                           height: `${tableHeight}px`,
                           width: "100%",
                        }}
                     >
                        <ServerSideDataTable.Table
                           pagination={showPagination}
                           paginationPageSize={PERSON_ASSIGNMENTS_PAGE_SIZE}
                           suppressColumnVirtualisation={false}
                           rowActionsConfig={{
                              sortable: false,
                           }}
                           onTableReady={(api) => {
                              tableApiRef.current = api;
                           }}
                        />
                     </Box>
                     <Flex flex="0 1 0px" alignItems="stretch">
                        <ServerSideDataTable.ContextPanel style={{ marginLeft: "16px" }} />
                     </Flex>
                  </Flex>
               </ServerSideDataTable>
            )}
         </Box>
      </Card>
   );
};

// Wrap in React.memo to prevent re-renders when parent re-renders with same props
export const PastAssignmentsTable = React.memo(
   PastAssignmentsTableComponent,
   (prevProps, nextProps) => {
      // Re-render only if personId or refetchAssignments changes
      // projectTearsheetDispatch is stored in a ref, so changes don't need to trigger re-render
      return (
         prevProps.personId === nextProps.personId &&
         prevProps.refetchAssignments === nextProps.refetchAssignments
      );
   },
);
